import { zodResolver } from "@hookform/resolvers/zod"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"
import z from "zod"

import DateTimePicker from "@/components/DateTimePicker"
import { Button } from "@/components/ui/button"
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field"

const formSchema = z.object({
  start: z.date(),
  end: z.date(),
})

interface ExportDialogProps {
  onExport: (data: z.infer<typeof formSchema>) => void
}

export default function KPIExportDialog({ onExport }: ExportDialogProps) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      start: new Date(Date.now() - 24 * 60 * 60 * 1000), // 24 hours ago
      end: new Date(),
    },
  })

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      toast("Export initiated")
      await onExport(data)
      toast("Export complete")
    } catch (error) {
      toast("Failed to initiate export")
      console.error("Export error:", error)
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Export Data</DialogTitle>
      </DialogHeader>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup className="gap-3">
          <Controller
            name="start"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>Start Date</FieldLabel>
                <DateTimePicker value={field.value} onChange={field.onChange} dateOnly />
                <FieldDescription>Start date for export range</FieldDescription>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />
          <Controller
            name="end"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>End Date</FieldLabel>
                <DateTimePicker value={field.value} onChange={field.onChange} dateOnly />
                <FieldDescription>End date for export range</FieldDescription>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />
        </FieldGroup>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button type="submit">Export</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
